import React, { useState } from "react";
import { Plus, Keyboard } from "lucide-react";
import API from "../../services/api";
import { errMsg, addDay, prettyDate } from "../runsheets/util";
import { DAY_TYPES, fmtHours } from "./util";

/* Keying an offsider's fortnight in is the operator's daily job, so this form
 * never closes between days: it keeps the person and rolls the date forward
 * after each save, and the crew member it last used survives the form being
 * collapsed (the draft lives in the parent). */

const INPUT = "bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500 w-full";
const TIME_INPUT = `${INPUT} [&::-webkit-calendar-picker-indicator]:invert`;
const LABEL = "block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1";

export default function ManualEntry({ crew, draft, setDraft, onChanged, periodStatus }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [recent, setRecent] = useState([]);

  const set = (patch) => setDraft({ ...draft, ...patch });

  // One break, recorded as its start and finish (paid, never deducted). Both
  // blank means no break; one blank is a half-typed break and cannot be sent,
  // because the server refuses a break without a finish. More breaks on the
  // same day are added in the day editor.
  const breakStart = draft.break_start || "";
  const breakFinish = draft.break_finish || "";
  const halfBreak = Boolean(breakStart) !== Boolean(breakFinish);
  const breaks = breakStart && breakFinish ? [{ start: breakStart, finish: breakFinish }] : [];

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (halfBreak) return;
    setBusy(true);
    setError(null);
    try {
      const res = await API.post("/time/shifts", {
        person_id: draft.person_id,
        work_date: draft.work_date,
        start: draft.start,
        finish: draft.finish || null,
        day_type: draft.day_type,
        note: draft.note || null,
        ...(breaks.length ? { breaks } : {}),
      });
      const saved = res.data?.shift || {};
      setRecent([{
        key: saved.id || `${draft.person_id}-${draft.work_date}`,
        who: saved.person_name || crew.find((c) => String(c.id) === String(draft.person_id))?.name || "crew",
        day: draft.work_date,
        hours: saved.paid_hours,
      }, ...recent].slice(0, 6));
      // Same person, next day, same times: a fortnight of an offsider's hours
      // is usually the same shift repeated, and retyping it is where the
      // mistakes come from.
      setDraft({ ...draft, work_date: addDay(draft.work_date, 1), note: "" });
      await onChanged();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-slate-900/30 p-4 rounded-xl border border-slate-800/60 space-y-3">
      <p className="text-xs font-semibold text-slate-300 flex items-center gap-2">
        <Keyboard size={14} className="text-indigo-400" /> Key a day in by hand
        <span className="text-slate-500 font-normal">— for crew with no login, or a punch that never left the phone</span>
      </p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div className="md:col-span-2">
          <span className={LABEL}>Crew member</span>
          <select className={`${INPUT} cursor-pointer`} value={draft.person_id} onChange={(e) => set({ person_id: e.target.value })}>
            <option value="">Choose crew…</option>
            {crew.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.role}</option>)}
          </select>
        </div>
        <div>
          <span className={LABEL}>Date worked (NZ)</span>
          <input type="date" className={TIME_INPUT} value={draft.work_date} onChange={(e) => set({ work_date: e.target.value })} />
        </div>
        <div>
          <span className={LABEL}>Day type</span>
          <select className={`${INPUT} cursor-pointer`} value={draft.day_type} onChange={(e) => set({ day_type: e.target.value })}>
            {DAY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <span className={LABEL}>Start</span>
          <input type="time" className={TIME_INPUT} value={draft.start} onChange={(e) => set({ start: e.target.value })} />
        </div>
        <div>
          <span className={LABEL}>Finish</span>
          <input type="time" className={TIME_INPUT} value={draft.finish} onChange={(e) => set({ finish: e.target.value })} />
        </div>
        <div>
          <span className={LABEL}>Break start (paid)</span>
          <input type="time" className={TIME_INPUT} value={breakStart} onChange={(e) => set({ break_start: e.target.value })} />
        </div>
        <div>
          <span className={LABEL}>Break finish</span>
          <input type="time" className={TIME_INPUT} value={breakFinish} onChange={(e) => set({ break_finish: e.target.value })} />
        </div>
        <input className={`${INPUT} md:col-span-3`} placeholder="Note (optional)"
          value={draft.note} onChange={(e) => set({ note: e.target.value })} />
        <button type="submit" disabled={busy || halfBreak || !draft.person_id || !draft.work_date || !draft.start}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 font-bold transition-all flex items-center justify-center gap-1.5 shadow w-full disabled:opacity-50">
          <Plus size={14} /> {busy ? "Saving…" : "Save & next day"}
        </button>
      </div>

      <p className="text-[10px] text-slate-500">
        A break is recorded as its start and finish. Leave both blank for no break — breaks are paid and are never deducted.
        {halfBreak && <span className="text-amber-400"> Enter both the break start and finish, or clear both.</span>}
      </p>

      {periodStatus === "approved" && (
        <p className="text-[10px] text-amber-400">This fortnight is approved. A day added now is locked as soon as it is saved.</p>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-300 break-words">{error}</div>
      )}

      {recent.length > 0 && (
        <div className="text-[11px] text-slate-400 space-y-1 pt-1 border-t border-slate-800">
          {recent.map((r) => (
            <div key={r.key} className="flex items-center gap-2">
              <span className="text-emerald-400">saved</span>
              <span className="text-slate-300 font-semibold">{r.who}</span>
              <span>{prettyDate(r.day)}</span>
              <span className="font-mono text-slate-500">{fmtHours(r.hours)} h</span>
            </div>
          ))}
        </div>
      )}
    </form>
  );
}
